import classNames from "classnames";
import React, { useEffect, useRef } from "react";

import { useSelector } from "../../utils/hooks";
import strings from "../../utils/strings";

import { DRAWER_HEADING_ID } from "./Header.utils";

interface DrawerPropsI {
	children: React.ReactNode;
}

export const Drawer = ({ children }: DrawerPropsI) => {
	const isVisible = useSelector((state) => state.popup.products.isVisible);
	const showSubscriptionDataOnly = useSelector(
		(state) => state.popup.showSubscriptionDataOnly,
	);

	const drawerRef = useRef<HTMLDivElement>(null);
	const previouslyFocusedElement = useRef<HTMLElement | null>(null);
	const focusableElementsRef = useRef<HTMLElement[]>([]);
	const focusTimeoutRef = useRef<number | null>(null);

	useEffect(() => {
		if (isVisible) {
			const updateFocusableElements = () => {
				focusableElementsRef.current = Array.from(
					drawerRef.current?.querySelectorAll<HTMLElement>(
						'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
					) || [],
				).filter((element) => {
					return !element.hasAttribute("disabled") && element.tabIndex >= 0;
				});
			};

			previouslyFocusedElement.current = document.activeElement as HTMLElement;
			updateFocusableElements();

			focusTimeoutRef.current = window.setTimeout(() => {
				updateFocusableElements();

				if (focusableElementsRef.current.length > 0) {
					focusableElementsRef.current[0].focus();
				} else {
					drawerRef.current?.focus();
				}
			}, 100);

			const handleKeyDown = (e: KeyboardEvent) => {
				if (e.key === "Tab") {
					const focusableElements = focusableElementsRef.current;

					if (focusableElements.length === 0) return;

					const firstElement = focusableElements[0];
					const lastElement = focusableElements[focusableElements.length - 1];

					if (e.shiftKey) {
						// Shift + Tab
						if (document.activeElement === firstElement) {
							e.preventDefault();
							(lastElement as HTMLElement).focus();
						}
					} else {
						// Tab
						if (document.activeElement === lastElement) {
							e.preventDefault();
							(firstElement as HTMLElement).focus();
						}
					}
				}
			};

			const observer = new MutationObserver(() => {
				updateFocusableElements();
			});

			if (drawerRef.current) {
				observer.observe(drawerRef.current, {
					attributes: true,
					attributeFilter: ["disabled", "href", "tabindex"],
					childList: true,
					subtree: true,
				});
			}

			document.addEventListener("keydown", handleKeyDown);

			return () => {
				if (focusTimeoutRef.current) {
					window.clearTimeout(focusTimeoutRef.current);
					focusTimeoutRef.current = null;
				}
				focusableElementsRef.current = [];
				observer.disconnect();
				document.removeEventListener("keydown", handleKeyDown);
			};
		} else {
			if (focusTimeoutRef.current) {
				window.clearTimeout(focusTimeoutRef.current);
				focusTimeoutRef.current = null;
			}
			focusableElementsRef.current = [];

			// Restore focus to the previously focused element
			if (previouslyFocusedElement.current) {
				previouslyFocusedElement.current.focus();
				previouslyFocusedElement.current = null;
			}
		}
	}, [isVisible]);

	return (
		<div
			ref={drawerRef}
			aria-hidden={!isVisible}
			aria-label={showSubscriptionDataOnly ? strings.popupAddToCart : undefined}
			aria-labelledby={
				showSubscriptionDataOnly ? undefined : DRAWER_HEADING_ID
			}
			aria-modal="true"
			className={classNames({
				"addtocart-drawer-container": true,
				caas: true,
				"show-element": isVisible,
			})}
			role="dialog"
			tabIndex={-1}
		>
			{children}
		</div>
	);
};
